import { makeStyles } from '@material-ui/core/styles';
import { MEDIA_QUERY } from 'utils/enum/Core';

const useTimeClockHomeStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.background.default,
        maxHeight: '94vh',
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        padding: theme.spacing(3),
        [theme.breakpoints.up('lg')]: {
            padding: theme.spacing(5),
        },
        [theme.breakpoints.down('sm')]: {
            maxHeight: 'calc(100vh - 64px)',
            minHeight: 'auto',
        },
    },
    container: {
        maxWidth: 1600,
        margin: '0 auto',
        width: '100%',
    },
    mainContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        flexGrow: 1,
        [theme.breakpoints.down('sm')]: {
            minHeight: 'auto',
        },
    },
    headerCard: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        padding: theme.spacing(4),
        [theme.breakpoints.up('lg')]: {
            padding: theme.spacing(6),
        },
        [theme.breakpoints.down(MEDIA_QUERY.W_430)]: {
            padding: theme.spacing(1.5),
        },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        boxShadow: theme.shadows[1],
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(0.75, 2),
        borderRadius: theme.spacing(2),
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.grey[600],
        marginBottom: theme.spacing(2),
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
    },
    statusBadgeActive: {
        backgroundColor: '#d4f5e7',
        color: '#0a7e42',
    },
    statusDot: {
        width: 8,
        height: 8,
        backgroundColor: theme.palette.grey[400],
        borderRadius: '50%',
        animation: '$pulse 2s infinite',
    },
    statusDotActive: {
        backgroundColor: '#0a7e42',
    },
    '@keyframes pulse': {
        '0%': {
            opacity: 1,
        },
        '50%': {
            opacity: 0.5,
        },
        '100%': {
            opacity: 1,
        },
    },
    clock: {
        fontSize: 72,
        [theme.breakpoints.up('lg')]: {
            fontSize: 96,
        },
        [theme.breakpoints.down(MEDIA_QUERY.W_430)]: {
            fontSize: 58,
        },
        fontWeight: 900,
        marginBottom: theme.spacing(4),
        marginTop: theme.spacing(4),
        color: theme.palette.grey[800],
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '-0.02em',
    },
    startButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontWeight: 700,
        fontSize: 18,
        display: 'flex',
        alignItems: 'center',
        marginTop: theme.spacing(2),
        boxShadow: `0 8px 16px ${theme.palette.primary.main}26`,
        transition: 'all 0.3s ease',
        '& .MuiButton-startIcon': {
            marginRight: theme.spacing(1.5),
        },
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            transform: 'scale(1.02)',
            boxShadow: `0 12px 24px ${theme.palette.primary.main}40`,
        },
        '&:active': {
            transform: 'scale(0.95)',
        },
    },
    stopButton: {
        backgroundColor: theme.palette.error.main,
        boxShadow: `0 8px 16px ${theme.palette.error.main}26`,
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
            boxShadow: `0 12px 24px ${theme.palette.error.main}40`,
        },
    },
    leaveForDayNotice: {
        marginTop: theme.spacing(2),
        width: '100%',
        maxWidth: 680,
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.info.light}`,
        backgroundColor: 'rgba(2, 136, 209, 0.1)',
        color: theme.palette.info.dark,
        padding: theme.spacing(1.5, 2),
        display: 'flex',
        gap: theme.spacing(1.5),
        textAlign: 'left',
        alignItems: 'center',
    },
    leaveForDayIcon: {
        color: theme.palette.info.main,
        marginTop: 1,
    },
    leaveForDayText: {
        fontSize: 14,
        fontWeight: 600,
        lineHeight: 1.4,
    },
    dashboardGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        [theme.breakpoints.up('lg')]: {
            gridTemplateColumns: '2fr 1fr',
        },
        gap: theme.spacing(3),
        flexGrow: 1,
        alignItems: 'stretch',
        height: 720,
        minHeight: 0,
        [theme.breakpoints.down('sm')]: {
            height: 'auto',
            minHeight: 'auto',
            gridTemplateColumns: '1fr',
        },
    },
    actionGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(3),
    },
    leftColumn: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        [theme.breakpoints.down('sm')]: {
            height: 'auto',
        },
    },
    actionButton: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(4),
        color: '#FFFFFF',
        border: 'none',
        borderRadius: '6px',
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08), 0 1px 3px rgba(0,0,0,0.18)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '& $actionIcon': {
            color: '#FFFFFF',
        },
        '& $actionLabel': {
            color: '#FFFFFF',
        },
        '&:hover': {
            filter: 'brightness(1.06)',
        },
        gap: theme.spacing(2),
    },
    actionButtonLunch: {
        backgroundColor: '#0F63A8',
    },
    actionButtonBreak: {
        backgroundColor: '#20B887',
    },
    actionButtonMedical: {
        backgroundColor: '#C51621',
    },
    actionButtonVacation: {
        backgroundColor: '#8126D2',
    },
    actionButtonOther: {
        backgroundColor: '#1B7E7B',
    },
    actionIcon: {
        fontSize: 32,
        color: '#FFFFFF',
    },
    actionLabel: {
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: '#FFFFFF',
    },
    logCard: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: theme.shadows[1],
        flexGrow: 1,
        minHeight: 0,
        flex: 1,
        [theme.breakpoints.down('sm')]: {
            flex: 'initial',
            height: 'auto',
        },
    },
    logHeader: {
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: 'rgba(248, 250, 252, 0.7)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    logTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    logSearchWrapper: {
        position: 'relative',
        width: 256,
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
    logSearchIcon: {
        position: 'absolute',
        left: theme.spacing(1.2),
        top: '50%',
        transform: 'translateY(-50%)',
        color: theme.palette.grey[500],
        fontSize: 18,
        pointerEvents: 'none',
    },
    logSearchInput: {
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.spacing(1),
            fontSize: 13,
            backgroundColor: theme.palette.background.paper,
            '& fieldset': {
                borderColor: theme.palette.grey[300],
            },
            '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
            },
        },
        '& .MuiOutlinedInput-input': {
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            paddingRight: theme.spacing(1.5),
            paddingLeft: theme.spacing(4.5),
        },
    },
    logContent: {
        overflowY: 'auto',
        flex: 1,
        minHeight: 0,
        WebkitOverflowScrolling: 'touch',
        [theme.breakpoints.down('sm')]: {
            maxHeight: '52vh',
        },
    },
    logItem: {
        padding: theme.spacing(2, 3),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    logItemActive: {
        backgroundColor: 'rgba(59, 130, 246, 0.08)',
    },
    logItemPast: {
        opacity: 0.65,
    },
    logItemContent: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.8),
        flex: 1,
    },
    logAvatar: {
        width: 40,
        height: 40,
    },
    logAvatarActive: {
        backgroundColor: '#d4f5e7',
    },
    logAvatarPast: {
        backgroundColor: '#f5f5f5',
    },
    logIconActive: {
        color: '#0a7e42',
    },
    logIconPast: {
        color: '#666',
    },
    logItemInner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: theme.spacing(2),
    },
    logText: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    logAction: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    logUserName: {
        fontSize: 12,
        color: theme.palette.grey[500],
        marginTop: theme.spacing(0.5),
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: 700,
    },
    logTimeWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
    },
    logTime: {
        fontSize: 13,
        color: theme.palette.text.secondary,
    },
    sidebar: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: theme.shadows[1],
        minHeight: 0,
        height: '100%',
        [theme.breakpoints.down('sm')]: {
            height: 'auto',
        },
    },
    sidebarHeader: {
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[50],
    },
    sidebarTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    tabsContainer: {
        display: 'flex',
        padding: theme.spacing(0.5),
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.spacing(0.5),
    },
    tabButton: {
        flex: 1,
        padding: theme.spacing(1, 1.5),
        fontSize: '12px',
        fontWeight: 700,
        borderRadius: theme.spacing(0.5),
        transition: 'all 0.2s ease',
        border: 'none',
        backgroundColor: 'transparent',
        color: theme.palette.grey[600],
        cursor: 'pointer',
        '&:hover': {
            color: theme.palette.grey[700],
        },
    },
    tabButtonActive: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.primary.main,
        boxShadow: theme.shadows[2],
    },
    sidebarContent: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        flex: 1,
        minHeight: 0,
        height: '100%',
        [theme.breakpoints.down('sm')]: {
            height: 'auto',
        },
    },
    periodList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        [theme.breakpoints.down('sm')]: {
            maxHeight: '52vh',
        },
    },
    periodOption: {
        width: '100%',
        textAlign: 'left',
        padding: theme.spacing(1.5, 2),
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: '#f3f7fc',
        },
    },
    periodOptionActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#f3f7fc',
        color: theme.palette.primary.main,
    },
    periodSelectMobile: {
        width: '100%',
        padding: theme.spacing(1, 1.5),
        borderRadius: theme.spacing(0.75),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            borderColor: theme.palette.primary.main,
        },
        '&:focus': {
            outline: 'none',
            borderColor: theme.palette.primary.main,
        },
    },
    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    inputLabel: {
        fontSize: '11px',
        fontWeight: 700,
        color: theme.palette.grey[600],
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: theme.spacing(0.5),
    },
    inputField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.spacing(0.75),
            backgroundColor: theme.palette.grey[50],
            fontSize: 14,
            '& fieldset': {
                borderColor: theme.palette.divider,
            },
            '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
            },
        },
    },
    generateButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontWeight: 700,
        padding: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1.5),
        boxShadow: theme.shadows[2],
        transition: 'all 0.2s ease',
        '& .MuiButton-startIcon': {
            marginRight: theme.spacing(1.5),
        },
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            transform: 'translateY(-1px)',
        },
    },
    yearFilterContainer: {
        marginBottom: theme.spacing(2),
    },
    yearFilterLabel: {
        fontSize: '11px',
        fontWeight: 700,
        color: theme.palette.grey[600],
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: theme.spacing(0.75),
        display: 'block',
    },
    yearFilterSelect: {
        width: '100%',
        padding: theme.spacing(1, 1.5),
        borderRadius: theme.spacing(0.75),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            borderColor: theme.palette.primary.main,
        },
        '&:focus': {
            outline: 'none',
            borderColor: theme.palette.primary.main,
        },
    },
    dialogSectionTitle: {
        marginBottom: 12,
        fontWeight: 600,
    },
    dialogSectionTitleSpaced: {
        marginTop: 12,
        marginBottom: 12,
        fontWeight: 600,
    },
    dialogReasonField: {
        marginTop: 12,
    },
}));

const useTimeClockLayoutStyles = makeStyles((theme) => ({
    layoutRoot: {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    layoutHeader: {
        height: 64,
        backgroundColor: '#0A192F',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(0, 3),
        flexShrink: 0,
        zIndex: 50,
    },
    layoutUserInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    layoutAvatar: {
        width: 40,
        height: 40,
        border: '1px solid',
        borderColor: theme.palette.grey[700],
        boxShadow: theme.shadows[1],
    },
    layoutUserName: {
        fontWeight: 'bold',
        color: 'white',
    },
    layoutUserCaption: {
        color: theme.palette.text.secondary,
    },
    layoutBrand: {
        fontWeight: 'bold',
        color: 'white',
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
    layoutLogoutButton: {
        minWidth: 0,
        padding: 8,
        borderRadius: '50%',
    },
    layoutMain: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f8f9fb',
    },
}));

const useTimeClockIPAccessDeniedStyles = makeStyles((theme) => ({
    accessDeniedContainer: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(3),
        backgroundColor: '#f8f9fb',
    },
    accessDeniedCard: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        maxWidth: 600,
        width: '100%',
    },
    accessDeniedIconWrapper: {
        width: 96,
        height: 96,
        backgroundColor: '#ffd2cd',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: theme.spacing(2),
    },
    accessDeniedIcon: {
        fontSize: 48,
        color: '#cb2f2f',
    },
    accessDeniedDivider: {
        margin: theme.spacing(2, 0),
        width: '100%',
    },
    accessDeniedTitle: {
        fontWeight: 'bold',
    },
}));

export { useTimeClockHomeStyles, useTimeClockLayoutStyles, useTimeClockIPAccessDeniedStyles };
